import React, { useRef, useState, useEffect } from "react";
import Table from "../../../core/common/dataTable/index";
import CommonSelect from "../../../core/common/commonSelect";
import { Link, useSearchParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import TooltipOption from "../../../core/common/tooltipOption";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import {
  getAllAssignments,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  getAllNotes,
  createNote,
  updateNote,
  deleteNote,
  type Assignment,
  type Note,
  type AssignmentCreateData,
  type NoteCreateData,
  Course,
  toggleNoteStatus,
} from "../../../service/assignments-and-notes";
import { getAllCourses } from "../../../service/courses";
import { getAllClasses, getAllSections } from "../../../service/classes";
import { getAllUsers, USER } from "../../../service/user";
import { toast } from "react-toastify";
import { CLASSES_SORT_OPTIONS } from "../../../core/common/selectoption/selectoption";
import { useSelector } from "react-redux";
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";
import { isTeacher, isStudent } from "../../../utils/rbacHelper";
import { selectUser } from "../../../core/data/redux/slice/user";
import { getUserEnrollment } from "../../../service/enrollment";

const ClassHomeWork = () => {
  const currentSession = useSelector(selectCurrentSession);
  const currentBranch = useSelector(selectCurrentBranch);
  const routes = all_routes;
  const user = useSelector(selectUser);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const classIdFromUrl = searchParams.get("class");

  const isUserTeacher = isTeacher(user);
  const isUserStudent = isStudent(user);
  const isNonTeacher = !isUserTeacher && !isUserStudent;

  const [activeTab, setActiveTab] = useState<"assignments" | "notes">(
    "assignments"
  );
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  const [currentFileInfo, setCurrentFileInfo] = useState<{
    name: string;
    url: string | null;
  }>({
    name: "No file attached",
    url: null,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
  });

  const [studentClassId, setStudentClassId] = useState<string>("");
  const [studentSectionId, setStudentSectionId] = useState<string>("");
  const [studentClassName, setStudentClassName] =
    useState<string>("Student's Class");
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);

  const [filters, setFilters] = useState({
    class: classIdFromUrl || "",
    section: "",
    course: "",
    type: "",
  });

  const [sortBy, setSortBy] = useState(CLASSES_SORT_OPTIONS[0]);
  const [searchText, setSearchText] = useState("");

  const [classes, setClasses] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [allCourses, setAllCourses] = useState<any[]>([]);
  const coursesPopulatedRef = useRef(false);
  const [teacherOptions, setTeacherOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [classCourses, setClassCourses] = useState<any[]>([]);

  const [assignmentForm, setAssignmentForm] = useState({
    title: "",
    type: "assignment" as "assignment" | "project" | "exam" | "other",
    class: classIdFromUrl || "",
    section: "",
    course: "",
    due_date: "",
    feedback: "",
    status: "active",
  });

  const initialTeacherValue = isUserTeacher ? user._id : "";

  const [noteForm, setNoteForm] = useState({
    teacher: initialTeacherValue,
    teacherName: "",
    class: classIdFromUrl || "",
    section: "",
    course: "",
    status: "active",
    title: "",
  });

  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(
    null
  );
  const [editingNote, setEditingNote] = useState<Note | null>(null);

  const fetchStudentEnrollment = async () => {
    if (isUserStudent && user._id && currentSession?._id) {
      try {
        setEnrollmentLoading(true);
        const enrollmentResponse = await getUserEnrollment(
          user._id,
          currentSession?._id
        );

        if (enrollmentResponse.success && enrollmentResponse.data) {
          const enrollmentData = enrollmentResponse.data;
          const classId = enrollmentData.class?._id || "";
          const sectionId = enrollmentData.section?._id || "";
          const className = enrollmentData.class?.name || "";
          if (classId) {
            setStudentClassId(classId);
            setStudentSectionId(sectionId);
            setStudentClassName(className);

            setFilters((prev) => ({
              ...prev,
              class: classId,
              section: sectionId,
            }));

            setAssignmentForm((prev) => ({
              ...prev,
              class: classId,
              section: sectionId,
            }));

            setNoteForm((prev) => ({
              ...prev,
              class: classId,
              section: sectionId,
            }));

            await fetchClassCourses(classId);
          } else {
            toast.warning("No class enrollment found for current session");
          }
        } else {
          toast.warning("Unable to load student enrollment details");
        }
      } catch (error) {
        console.error("Error fetching student enrollment:", error);
        toast.error("Failed to load student details");
      } finally {
        setEnrollmentLoading(false);
      }
    }
  };

  const getCleanFileName = (url: string): string => {
    if (!url) return "Current File";

    try {
      const urlWithoutParams = url.split("?")[0];
      const fileName = urlWithoutParams.split("/").pop() || "Current File";

      const cleanName = decodeURIComponent(fileName);

      return cleanName
        .replace(/^\d+_/, "")
        .replace(/%20/g, " ")
        .replace(/_/g, " ");
    } catch (error) {
      console.error("Error parsing file name:", error);
      return "Current File";
    }
  };

  const fetchDropdownData = async () => {
    try {
      if (!isUserStudent) {
        const classesResponse = await getAllClasses({ limit: 100 });
        setClasses(classesResponse.classes || []);

        const sectionsResponse = await getAllSections({ session: currentSession?._id });
        let sectionsData = [];
        if (Array.isArray(sectionsResponse)) {
          sectionsData = sectionsResponse;
        } else if (Array.isArray(sectionsResponse?.data)) {
          sectionsData = sectionsResponse.data;
        } else if (Array.isArray(sectionsResponse?.sections)) {
          sectionsData = sectionsResponse.sections;
        } else if (
          sectionsResponse?.success &&
          Array.isArray(sectionsResponse.data)
        ) {
          sectionsData = sectionsResponse.data;
        }
        setSections(sectionsData);
      }

      // Fetch all courses from courses API directly
      try {
        const coursesResponse = await getAllCourses({});
        setAllCourses(coursesResponse.data || []);
      } catch (error) {
        console.error("Error fetching courses:", error);
        setAllCourses([]);
      }

      if (isUserTeacher || isUserStudent) {
        const teacherOptionsData = isUserTeacher
          ? [
              {
                value: user._id,
                label: `${user.firstName} ${user.lastName}`,
              },
            ]
          : [];
        setTeacherOptions(teacherOptionsData);
      } else {
        const usersResponse = await getAllUsers({ status: "active" });

        const teacherOptionsData = usersResponse.data
          .filter((user: USER) => {
            return (
              typeof user.role === "object" &&
              user.role !== null &&
              "name" in user.role &&
              user.role.name.toLowerCase() === "teacher"
            );
          })
          .map((user: USER) => {
            return {
              value: user._id,
              label: `${user.firstName} ${user.lastName}`,
            };
          });

        setTeacherOptions(teacherOptionsData);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load dropdown data");
    }
  };

  const fetchClassCourses = async (classId: string) => {
    try {
      if (!classId) {
        setClassCourses([]);
        return [];
      }

      // First, ensure classes are loaded
      let selectedClass = classes.find((cls) => cls._id === classId);
      
      // If class not found in state, fetch it
      if (!selectedClass && classes.length === 0) {
        try {
          const classesResponse = await getAllClasses({ limit: 100 });
          const allClasses = classesResponse.classes || [];
          selectedClass = allClasses.find((cls: any) => cls._id === classId);
          if (selectedClass && classes.length === 0) {
            setClasses(allClasses);
          }
        } catch (error) {
          console.error("Error fetching classes:", error);
        }
      }

      if (
        selectedClass &&
        selectedClass.course &&
        selectedClass.course.length > 0
      ) {
        const courseIds = selectedClass.course;
        
        // Use allCourses if available, otherwise fetch
        let coursesToUse = allCourses;
        
        if (coursesToUse.length === 0) {
          try {
            const coursesResponse = await getAllCourses({});
            if (coursesResponse.success && coursesResponse.data) {
              coursesToUse = coursesResponse.data;
              setAllCourses(coursesToUse);
            }
          } catch (error) {
            console.error("Error fetching courses from API:", error);
          }
        }
        
        if (coursesToUse.length > 0) {
          // Filter courses by the IDs from class.course array
          const courseDetails = coursesToUse
            .filter((course) => courseIds.includes(course._id))
            .map((course) => ({
              _id: course._id,
              name: course.title || course.courseIdentifier,
              courseIdentifier: course.courseIdentifier,
              title: course.title,
            }));
          
          if (courseDetails.length > 0) {
            setClassCourses(courseDetails);
            return courseDetails;
          }
        }
        
        // Fallback: if courses API fails, use basic course objects
        const basicCourses = courseIds.map((courseId: string) => ({
          _id: courseId,
          name: `Course ${courseId.substring(0, 8)}...`,
        }));

        setClassCourses(basicCourses);
        return basicCourses;
      }

      setClassCourses([]);
      return [];
    } catch (error) {
      console.error("Error fetching class courses:", error);
      setClassCourses([]);
      return [];
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const commonParams: any = {
        page: pagination.currentPage,
        limit: pagination.limit,
        searchText,
        ...sortBy.value,
      };

      if (isUserTeacher && user._id) {
        commonParams.teacher = user._id;
      }

      if (isUserStudent) {
        if (studentClassId) {
          commonParams.class = studentClassId;
          commonParams.section = studentSectionId;
        } else {
          setAssignments([]);
          setNotes([]);
          setPagination({
            currentPage: 1,
            totalPages: 1,
            total: 0,
            limit: 10,
          });
          return;
        }
      } else {
        if (filters.class) commonParams.class = filters.class;
        if (filters.section) commonParams.section = filters.section;
        if (filters.course) commonParams.course = filters.course;
        if (filters.type && activeTab === "assignments")
          commonParams.type = filters.type;
      }

      if (currentSession?._id) {
        commonParams.session = currentSession?._id;
      }

      if (activeTab === "assignments") {
        const response = await getAllAssignments(commonParams);
        
        // Reset the populated flag when fetching new data
        coursesPopulatedRef.current = false;
        
        // Populate course data if course is just an ID
        const assignmentsWithCourses = (response.data || []).map((assignment: any) => {
          if (assignment.course) {
            // If course is a string ID, find the course object
            if (typeof assignment.course === "string") {
              const courseObj = allCourses.find((c: any) => c._id === assignment.course);
              if (courseObj) {
                return {
                  ...assignment,
                  course: {
                    _id: courseObj._id,
                    name: courseObj.title || courseObj.name || courseObj.courseIdentifier,
                    code: courseObj.courseIdentifier,
                    title: courseObj.title,
                  },
                };
              }
            }
            // If course is an object but missing name/title, populate it
            else if (assignment.course && typeof assignment.course === "object" && !assignment.course.name && !assignment.course.title) {
              const courseObj = allCourses.find((c: any) => c._id === assignment.course._id);
              if (courseObj) {
                return {
                  ...assignment,
                  course: {
                    ...assignment.course,
                    name: courseObj.title || courseObj.name || courseObj.courseIdentifier,
                    title: courseObj.title,
                    code: courseObj.courseIdentifier,
                  },
                };
              }
            }
          }
          return assignment;
        });

        setAssignments(assignmentsWithCourses);
        setPagination(
          response.pagination || {
            currentPage: 1,
            totalPages: 1,
            total: response.data?.length || 0,
            limit: 10,
          }
        );
      } else {
        const response = await getAllNotes(commonParams);
        
        // Populate course data if course is just an ID
        const notesWithCourses = (response.data || []).map((note: any) => {
          if (note.course) {
            // If course is a string ID, find the course object
            if (typeof note.course === "string") {
              const courseObj = allCourses.find((c: any) => c._id === note.course);
              if (courseObj) {
                return {
                  ...note,
                  course: {
                    _id: courseObj._id,
                    name: courseObj.title || courseObj.name || courseObj.courseIdentifier,
                    code: courseObj.courseIdentifier,
                    title: courseObj.title,
                  },
                };
              }
            }
            // If course is an object but missing name/title, populate it
            else if (note.course && typeof note.course === "object" && !note.course.name && !note.course.title) {
              const courseObj = allCourses.find((c: any) => c._id === note.course._id);
              if (courseObj) {
                return {
                  ...note,
                  course: {
                    ...note.course,
                    name: courseObj.title || courseObj.name || courseObj.courseIdentifier,
                    title: courseObj.title,
                    code: courseObj.courseIdentifier,
                  },
                };
              }
            }
          }
          return note;
        });

        setNotes(notesWithCourses);
        setPagination(
          response.pagination || {
            currentPage: 1,
            totalPages: 1,
            total: response.data?.length || 0,
            limit: 10,
          }
        );
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      if (isUserStudent) {
        await fetchStudentEnrollment();
      }
      await fetchDropdownData();
    };
    initializeData();
  }, [currentSession?._id]);

  useEffect(() => {
    fetchData();
  }, [
    pagination.currentPage,
    filters,
    sortBy,
    searchText,
    activeTab,
    currentSession?._id,
    studentClassId,
    studentSectionId,
  ]);

  // Update assignments/notes with course data when courses are loaded (without refetching)
  useEffect(() => {
    if (allCourses.length > 0 && !coursesPopulatedRef.current) {
      let shouldUpdate = false;
      
      // Check if assignments need course population
      const needsUpdate = assignments.some((assignment: any) => {
        if (!assignment.course) return false;
        if (typeof assignment.course === "string") return true;
        if (assignment.course && typeof assignment.course === "object" && !assignment.course.name && !assignment.course.title) return true;
        return false;
      }) || notes.some((note: any) => {
        if (!note.course) return false;
        if (typeof note.course === "string") return true;
        if (note.course && typeof note.course === "object" && !note.course.name && !note.course.title) return true;
        return false;
      });

      if (needsUpdate && (assignments.length > 0 || notes.length > 0)) {
        shouldUpdate = true;
        coursesPopulatedRef.current = true;
      }

      if (shouldUpdate) {
        // Update assignments with course data
        if (assignments.length > 0) {
          const updatedAssignments = assignments.map((assignment: any) => {
            if (assignment.course) {
              // If course is a string ID, find the course object
              if (typeof assignment.course === "string") {
                const courseObj = allCourses.find((c: any) => c._id === assignment.course);
                if (courseObj) {
                  return {
                    ...assignment,
                    course: {
                      _id: courseObj._id,
                      name: courseObj.title || courseObj.name || courseObj.courseIdentifier,
                      code: courseObj.courseIdentifier,
                      title: courseObj.title,
                    },
                  };
                }
              }
              // If course is an object but missing name/title, populate it
              else if (assignment.course && typeof assignment.course === "object" && !assignment.course.name && !assignment.course.title) {
                const courseObj = allCourses.find((c: any) => c._id === assignment.course._id);
                if (courseObj) {
                  return {
                    ...assignment,
                    course: {
                      ...assignment.course,
                      name: courseObj.title || courseObj.name || courseObj.courseIdentifier,
                      title: courseObj.title,
                      code: courseObj.courseIdentifier,
                    },
                  };
                }
              }
            }
            return assignment;
          });
          setAssignments(updatedAssignments);
        }

        // Update notes with course data
        if (notes.length > 0) {
          const updatedNotes = notes.map((note: any) => {
            if (note.course) {
              // If course is a string ID, find the course object
              if (typeof note.course === "string") {
                const courseObj = allCourses.find((c: any) => c._id === note.course);
                if (courseObj) {
                  return {
                    ...note,
                    course: {
                      _id: courseObj._id,
                      name: courseObj.title || courseObj.name || courseObj.courseIdentifier,
                      code: courseObj.courseIdentifier,
                      title: courseObj.title,
                    },
                  };
                }
              }
              // If course is an object but missing name/title, populate it
              else if (note.course && typeof note.course === "object" && !note.course.name && !note.course.title) {
                const courseObj = allCourses.find((c: any) => c._id === note.course._id);
                if (courseObj) {
                  return {
                    ...note,
                    course: {
                      ...note.course,
                      name: courseObj.title || courseObj.name || courseObj.courseIdentifier,
                      title: courseObj.title,
                      code: courseObj.courseIdentifier,
                    },
                  };
                }
              }
            }
            return note;
          });
          setNotes(updatedNotes);
        }
      }
    }
  }, [allCourses.length, assignments.length, notes.length]); // Only update when courses are loaded, don't refetch

  useEffect(() => {
    if (classIdFromUrl) {
      setFilters((prev) => ({
        ...prev,
        class: classIdFromUrl,
      }));
      setAssignmentForm((prev) => ({
        ...prev,
        class: classIdFromUrl,
      }));
      setNoteForm((prev) => ({
        ...prev,
        class: classIdFromUrl,
      }));
    }
  }, [classIdFromUrl]);

  useEffect(() => {
    if (assignmentForm.class && classes.length > 0) {
      fetchClassCourses(assignmentForm.class);
    }
  }, [assignmentForm.class, classes, allCourses]);

  // Fetch courses when modal opens and class is selected
  useEffect(() => {
    const handleModalShow = () => {
      // Fetch courses when modal opens if class is already selected
      if (assignmentForm.class) {
        fetchClassCourses(assignmentForm.class);
      }
    };

    const modalElement = document.getElementById('add_home_work');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', handleModalShow);
      return () => {
        modalElement.removeEventListener('shown.bs.modal', handleModalShow);
      };
    }
  }, [assignmentForm.class]);

  useEffect(() => {
    if (noteForm.class) {
      fetchClassCourses(noteForm.class);
    }
  }, [noteForm.class, classes, allCourses]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSortSelect = (item: {
    label: string;
    value: { sortBy: string; sortOrder: string };
  }) => {
    setSortBy(item);
  };

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  const handleExportExcel = () => {
    const data = activeTab === "assignments" ? assignments : notes;
    const ws = XLSX.utils.json_to_sheet(
      data.map((item: any) => {
        if (activeTab === "assignments") {
          return {
            "Assignment ID": item.assignmentIdentifier || item._id,
            Title: item.title,
            Type: item.type,
            Class: item.class?.name || "N/A",
            Section: item.section?.name || "N/A",
            Course: item.course?.name || "N/A",
            "Due Date": item.due_date
              ? new Date(item.due_date).toLocaleDateString()
              : "N/A",
            Status: item.status,
            "Created Date": new Date(item.createdAt).toLocaleDateString(),
          };
        } else {
          return {
            "Note ID": item.notesIdentifier || item._id,
            Title: item.title || "N/A",
            Teacher: item.teacher?.email || "N/A",
            Class: item.class?.name || "N/A",
            Section: item.section?.name || "N/A",
            Course: item.course?.name || "N/A",
            Status: item.status,
            "Created Date": new Date(item.createdAt).toLocaleDateString(),
          };
        }
      })
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      activeTab === "assignments" ? "Assignments" : "Notes"
    );
    XLSX.writeFile(wb, `${activeTab}.xlsx`);
  };

  const classOptions = Array.isArray(classes)
    ? classes.map((cls) => ({
        value: cls._id,
        label: cls.name,
      }))
    : [];

  const sectionOptions = Array.isArray(sections)
    ? sections.map((section) => ({
        value: section._id,
        label: section.name,
      }))
    : [];

  // Courses for filters (can still be class-based)
  const courseOptions = classCourses.map((course) => ({
    value: course._id,
    label: course.name || `Course ${course._id.substring(0, 8)}...`,
  }));

  // Courses for create/edit forms - always populated directly from courses API
  const formCourseOptions = allCourses.map((course: any) => ({
    value: course._id,
    label: course.title || course.courseIdentifier || `Course ${course._id.substring(0, 8)}...`,
  }));

  const typeOptions = [
    { value: "assignment", label: "Assignment" },
    { value: "project", label: "Project" },
    { value: "exam", label: "Exam" },
    { value: "other", label: "Other" },
  ];

  const selectedClassName = isUserStudent
    ? studentClassName
    : classOptions.find(
        (cls) => cls.value === (filters.class || classIdFromUrl)
      )?.label || "Selected Class";

  const closeModal = (modalId: string) => {
    const el = document.getElementById(modalId);
    if (el) {
      el.setAttribute("data-bs-dismiss", "modal");
      el.click();
      el.removeAttribute("data-bs-dismiss");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Please select a valid file type (PDF, DOC, DOCX, PPT)");
        e.target.value = "";
        setSelectedFile(null);
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size should be less than 10MB");
        e.target.value = "";
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };

  const handleAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (!assignmentForm.course) {
        toast.error("Please select a course");
        return;
      }

      if (!selectedFile) {
        toast.error("Please select a file attachment");
        return;
      }

      console.log("DEBUG - Section value:", assignmentForm.section);
      const assignmentData: AssignmentCreateData & { file?: File } = {
        title: assignmentForm.title,
        type: assignmentForm.type,
        class: assignmentForm.class,
        section: assignmentForm.section || "",
        course: assignmentForm.course,
        due_date: assignmentForm.due_date,
        feedback: assignmentForm.feedback,
        file: selectedFile || undefined,
        session: currentSession?._id,
      };

      await createAssignment(assignmentData);
      closeModal("add_home_work");
      resetAssignmentForm();
      fetchData();
      toast.success("Assignment created successfully!");
    } catch (error: any) {
      console.error("Error creating assignment:", error);
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create assignment";

      toast.error(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAssignment) return;

    try {
      setLoading(true);

      if (!assignmentForm.course) {
        toast.error("Please select a course");
        return;
      }

      if (!currentFileInfo.url && !selectedFile) {
        toast.error("Please select a file attachment");
        return;
      }

      const assignmentData: AssignmentCreateData & { file?: File } = {
        title: assignmentForm.title,
        type: assignmentForm.type,
        class: assignmentForm.class,
        section: assignmentForm.section,
        course: assignmentForm.course,
        due_date: assignmentForm.due_date,
        feedback: assignmentForm.feedback,
        file: selectedFile || undefined,
      };

      await updateAssignment(editingAssignment._id, assignmentData);
      closeModal("edit_home_work");
      resetAssignmentForm();
      fetchData();
      toast.success("Assignment updated successfully!");
    } catch (error: any) {
      console.error("Error updating assignment:", error);
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create assignment";

      toast.error(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentDelete = async (id: string) => {
    try {
      setLoading(true);
      await deleteAssignment(id);
      closeModal("delete-modal");
      await fetchData();
      toast.success("Assignment deleted successfully!");
    } catch (error: any) {
      console.error("Error deleting assignment:", error);
      toast.error(error.response?.data?.error || "Failed to delete assignment");
    } finally {
      setLoading(false);
    }
  };

  const handleNoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (!noteForm.course) {
        toast.error("Please select a course");
        return;
      }

      if (!selectedFile) {
        toast.error("Please select a file attachment");
        return;
      }

      const noteData: NoteCreateData & { file?: File } = {
        title: noteForm.title,
        teacher: noteForm.teacher,
        class: noteForm.class,
        section: noteForm.section,
        course: noteForm.course,
        file: selectedFile || undefined,
        session: currentSession?._id,
      };

      await createNote(noteData);
      closeModal("add_note");
      resetNoteForm();
      fetchData();
      toast.success("Note created successfully!");
    } catch (error: any) {
      console.error("Error creating note:", error);
      toast.error(error.response?.data?.error || "Failed to create note");
    } finally {
      setLoading(false);
    }
  };

  const handleNoteUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingNote) return;

    try {
      setLoading(true);

      if (!noteForm.course) {
        toast.error("Please select a course");
        return;
      }

      if (!currentFileInfo.url && !selectedFile) {
        toast.error("Please select a file attachment");
        return;
      }

      const noteData: NoteCreateData & { file?: File } = {
        title: noteForm.title,
        teacher: noteForm.teacher,
        class: noteForm.class,
        section: noteForm.section,
        course: noteForm.course,
        file: selectedFile || undefined,
      };

      await updateNote(editingNote._id, noteData);
      closeModal("edit_note");
      resetNoteForm();
      fetchData();
      toast.success("Note updated successfully!");
    } catch (error: any) {
      console.error("Error updating note:", error);
      toast.error(error.response?.data?.error || "Failed to update note");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNoteStatus = async (id: string, currentStatus: string) => {
    try {
      setLoading(true);
      const response = await toggleNoteStatus(id);

      if (response.success) {
        const newStatus = currentStatus === "active" ? "inactive" : "active";

        setNotes((prevNotes) =>
          prevNotes.map((note) =>
            note._id === id
              ? {
                  ...note,
                  status: newStatus,
                }
              : note
          )
        );

        toast.success(
          `Note ${
            currentStatus === "active" ? "deactivated" : "activated"
          } successfully!`
        );
      } else {
        toast.error(response.message || "Failed to update note status");
      }
    } catch (error: any) {
      console.error("Error Message:", error.message);
      let errorMessage = "Failed to update note status";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNoteDelete = async (id: string) => {
    try {
      setLoading(true);
      await deleteNote(id);
      closeModal("delete-modal");
      await fetchData();
      toast.success("Note deleted successfully!");
    } catch (error: any) {
      console.error("Error deleting note:", error);
      toast.error(error.response?.data?.error || "Failed to delete note");
    } finally {
      setLoading(false);
    }
  };

  const handleEditAssignment = async (assignment: Assignment) => {
    setEditingAssignment(assignment);
    await fetchClassCourses(assignment.class._id);

    const courseId = assignment.course?._id || "";

    setAssignmentForm((prev) => ({
      ...prev,
      title: assignment.title,
      type: assignment.type,
      class: assignment.class._id,
      section: assignment.section?._id || "",
      course: courseId,
      due_date: assignment.due_date ? assignment.due_date.split("T")[0] : "",
      feedback: assignment.feedback || "",
      status: assignment.status,
    }));

    if (assignment.file_url) {
      const cleanFileName = getCleanFileName(assignment.file_url);
      setCurrentFileInfo({
        name: cleanFileName,
        url: assignment.file_url,
      });
    } else {
      setCurrentFileInfo({
        name: "No file attached",
        url: null,
      });
    }
    setSelectedFile(null);
    setFileInputKey(Date.now());
  };

  const handleEditNote = async (note: Note) => {
    setEditingNote(note);
    await fetchClassCourses(note.class._id);

    const courseId = note.course?._id || "";

    let teacherName = "";
    if (note.teacher) {
      if (typeof note.teacher === "object" && note.teacher !== null) {
        teacherName = `${note.teacher.firstName || ""} ${
          note.teacher.lastName || ""
        }`.trim();
      }
    }

    setNoteForm((prev) => ({
      ...prev,
      title: note.title || "",
      teacher: note.teacher?._id || initialTeacherValue,
      teacherName: teacherName,
      class: note.class._id,
      section: note.section._id,
      course: courseId,
      status: note.status,
    }));

    if (note.file_url) {
      const cleanFileName = getCleanFileName(note.file_url);
      setCurrentFileInfo({
        name: cleanFileName,
        url: note.file_url,
      });
    } else {
      setCurrentFileInfo({
        name: "No file attached",
        url: null,
      });
    }
    setSelectedFile(null);
    setFileInputKey(Date.now());
  };

  const resetAssignmentForm = () => {
    setAssignmentForm({
      title: "",
      type: "assignment",
      class: classIdFromUrl || "",
      section: "",
      course: "",
      due_date: "",
      feedback: "",
      status: "active",
    });
    setSelectedFile(null);
    setEditingAssignment(null);
    setCurrentFileInfo({ name: "No file attached", url: null });
    setFileInputKey(Date.now());
  };

  const resetNoteForm = () => {
    setNoteForm({
      title: "",
      teacher: initialTeacherValue,
      teacherName: isUserTeacher ? `${user.firstName} ${user.lastName}` : "",
      class: classIdFromUrl || "",
      section: "",
      course: "",
      status: "active",
    });
    setSelectedFile(null);
    setEditingNote(null);
    setCurrentFileInfo({ name: "No file attached", url: null });
    setFileInputKey(Date.now());
  };

  const showActions = !isUserStudent;

  const assignmentColumns = [
    {
      title: "ID",
      dataIndex: "assignmentIdentifier",
      render: (text: string, record: Assignment) => (
        <Link to="#" className="link-primary">
          {record.assignmentIdentifier || record._id}
        </Link>
      ),
      sorter: true,
    },
    {
      title: "Title",
      dataIndex: "title",
      sorter: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (type: string) => (
        <span
          className={`badge bg-${
            type === "assignment"
              ? "primary"
              : type === "project"
              ? "success"
              : type === "exam"
              ? "warning"
              : "info"
          }`}
        >
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </span>
      ),
    },
    {
      title: "Class",
      dataIndex: "class",
      render: (classObj: Assignment["class"]) => classObj?.name || "N/A",
    },
    {
      title: "Section",
      dataIndex: "section",
      render: (section: Assignment["section"]) => section?.name || "N/A",
    },
    {
      title: "Course",
      dataIndex: "course",
      render: (course: any) => {
        // Handle if course is a string ID
        if (typeof course === "string") {
          const courseObj = allCourses.find((c: any) => c._id === course);
          return courseObj?.title || courseObj?.name || courseObj?.courseIdentifier || "N/A";
        }
        // Handle if course is an object
        if (course && typeof course === "object") {
          return course.title || course.name || course.courseIdentifier || "N/A";
        }
        return "N/A";
      },
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      render: (date: string) =>
        date ? new Date(date).toLocaleDateString() : "-",
    },
    {
      title: "File",
      dataIndex: "file_url",
      render: (file_url: string) =>
        file_url ? (
          <a
            href={file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="link-primary"
          >
            <i className="ti ti-file me-1"></i>
            View File
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => (
        <span
          className={`badge badge-soft-${
            status === "submitted"
              ? "success"
              : status === "graded"
              ? "primary"
              : "warning"
          } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
      sorter: true,
    },
    ...(showActions
      ? [
          {
            title: "Action",
            dataIndex: "action",
            render: (text: string, record: Assignment) => (
              <div className="d-flex align-items-center">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-dots-vertical fs-14" />
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-right p-3">
                    <li>
                      <Link
                        className={`dropdown-item rounded-1 ${
                          user.permissions?.includes("class-assignment.edit") ||
                          !user.permissions?.length
                            ? ""
                            : "not-allowed"
                        }`}
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#edit_home_work"
                        onClick={(e) => {
                          if (
                            !user.permissions?.includes(
                              "class-assignment.edit"
                            ) &&
                            !!user.permissions?.length
                          ) {
                            e.preventDefault();
                            e.stopPropagation();
                          } else {
                            handleEditAssignment(record);
                          }
                        }}
                      >
                        <i className="ti ti-edit-circle me-2" />
                        Edit
                      </Link>
                    </li>
                    <li>
                      <Link
                        className={`dropdown-item rounded-1 text-danger ${
                          user.permissions?.includes(
                            "class-assignment.delete"
                          ) || !user.permissions?.length
                            ? ""
                            : "not-allowed"
                        }`}
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#delete-modal"
                        onClick={(e) => {
                          if (
                            !user.permissions?.includes(
                              "class-assignment.delete"
                            ) &&
                            !!user.permissions?.length
                          ) {
                            e.preventDefault();
                            e.stopPropagation();
                          } else {
                            setEditingAssignment(record);
                          }
                        }}
                      >
                        <i className="ti ti-trash-x me-2" />
                        Delete
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            ),
          },
        ]
      : []),
  ];

  const notesColumns = [
    {
      title: "ID",
      dataIndex: "notesIdentifier",
      render: (text: string, record: Note) => (
        <Link to="#" className="link-primary">
          {record.notesIdentifier || record._id}
        </Link>
      ),
      sorter: true,
    },
    {
      title: "Title",
      dataIndex: "title",
      sorter: true,
    },
    {
      title: "Teacher",
      dataIndex: "teacher",
      render: (teacher: Note["teacher"]) =>
        teacher ? `${teacher.firstName} ${teacher.lastName}` : "N/A",
    },
    {
      title: "Class",
      dataIndex: "class",
      render: (classObj: Note["class"]) => classObj?.name || "N/A",
    },
    {
      title: "Section",
      dataIndex: "section",
      render: (section: Note["section"]) => section?.name || "N/A",
    },
    {
      title: "Course",
      dataIndex: "course",
      render: (course: any) => {
        // Handle if course is a string ID
        if (typeof course === "string") {
          const courseObj = allCourses.find((c: any) => c._id === course);
          return courseObj?.title || courseObj?.name || courseObj?.courseIdentifier || "N/A";
        }
        // Handle if course is an object
        if (course && typeof course === "object") {
          return course.title || course.name || course.courseIdentifier || "N/A";
        }
        return "N/A";
      },
    },
    {
      title: "File",
      dataIndex: "file_url",
      render: (file_url: string) =>
        file_url ? (
          <a
            href={file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="link-primary"
          >
            <i className="ti ti-file me-1"></i>
            View File
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string, record: Note) => (
        <span
          className={`badge badge-soft-${
            status === "active" ? "success" : "danger"
          } d-inline-flex align-items-center`}
        >
          <i
            className={`ti ti-circle-filled fs-5 me-1 ${
              status === "active" ? "text-success" : "text-danger"
            }`}
          ></i>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
      sorter: true,
    },
    ...(showActions
      ? [
          {
            title: "Action",
            dataIndex: "action",
            render: (text: string, record: Note) => (
              <div className="d-flex align-items-center">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-dots-vertical fs-14" />
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-right p-3">
                    <li>
                      <Link
                        className={`dropdown-item rounded-1 ${
                          user.permissions?.includes("class-notes.edit") ||
                          !user.permissions?.length
                            ? ""
                            : "not-allowed"
                        }`}
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#edit_note"
                        onClick={(e) => {
                          if (
                            !user.permissions?.includes("class-notes.edit") &&
                            !!user.permissions?.length
                          ) {
                            e.preventDefault();
                            e.stopPropagation();
                          } else {
                            handleEditNote(record);
                          }
                        }}
                      >
                        <i className="ti ti-edit-circle me-2" />
                        Edit
                      </Link>
                    </li>
                    <li>
                      <button
                        className={`dropdown-item rounded-1 ${
                          user.permissions?.includes("class-notes.edit") ||
                          !user.permissions?.length
                            ? ""
                            : "not-allowed"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (
                            user.permissions?.includes("class-notes.edit") ||
                            !user.permissions?.length
                          ) {
                            handleToggleNoteStatus(record._id, record.status);
                          }
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          width: "100%",
                          textAlign: "left",
                          padding: "0.5rem 1rem",
                        }}
                      >
                        <i
                          className={`ti ti-toggle-${
                            record.status === "active"
                              ? "left text-danger"
                              : "right text-success"
                          } me-2`}
                        />
                        {record.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {isUserStudent
                ? `${selectedClassName} - ${
                    activeTab === "assignments" ? "My Assignments" : "My Notes"
                  }`
                : classIdFromUrl || filters.class
                ? `${selectedClassName} - ${
                    activeTab === "assignments" ? "Assignments" : "Notes"
                  }`
                : activeTab === "assignments"
                ? "Class Assignments"
                : "Teacher Notes"}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Academic</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isUserStudent
                    ? activeTab === "assignments"
                      ? "My Assignments"
                      : "My Notes"
                    : activeTab === "assignments"
                    ? "Class Assignments"
                    : "Teacher Notes"}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <TooltipOption
              onRefresh={fetchData}
              onExportPdf={handlePrint}
              onExportExcel={handleExportExcel}
            />
            {!isUserStudent && (
              <div className="mb-2">
                {activeTab === "assignments" ? (
                  <Link
                    to="#"
                    className={`btn btn-primary ${
                      user.permissions?.includes("class-assignment.create") ||
                      !user.permissions?.length
                        ? ""
                        : "not-allowed"
                    }`}
                    data-bs-toggle="modal"
                    data-bs-target="#add_home_work"
                    onClick={(e) => {
                      if (
                        !user.permissions?.includes(
                          "class-assignment.create"
                        ) &&
                        !!user.permissions?.length
                      ) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    <i className="ti ti-square-rounded-plus-filled me-2" />
                    Add Assignment
                  </Link>
                ) : (
                  isUserTeacher ||
                  (!user.permissions?.length && (
                    <Link
                      to="#"
                      className={`btn btn-primary ${
                        user.permissions?.includes("class-notes.create") ||
                        !user.permissions?.length
                          ? ""
                          : "not-allowed"
                      }`}
                      data-bs-toggle="modal"
                      data-bs-target="#add_note"
                      onClick={(e) => {
                        if (
                          !user.permissions?.includes("class-notes.create") &&
                          !!user.permissions?.length
                        ) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                    >
                      <i className="ti ti-square-rounded-plus-filled me-2" />
                      Add Note
                    </Link>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="card mb-3">
          <div className="card-body">
            <ul className="nav nav-tabs nav-tabs-bottom">
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    activeTab === "assignments" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("assignments")}
                >
                  <i className="ti ti-pencil me-2" />
                  {isUserStudent ? "My Assignments" : "Assignments"}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    activeTab === "notes" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("notes")}
                >
                  <i className="ti ti-notes me-2" />
                  {isUserStudent ? "My Notes" : "Notes"}
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Content based on active tab */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">
              {isUserStudent
                ? `${selectedClassName} - ${
                    activeTab === "assignments" ? "My Assignments" : "My Notes"
                  }`
                : classIdFromUrl || filters.class
                ? `${selectedClassName} - ${
                    activeTab === "assignments" ? "Assignments" : "Notes"
                  }`
                : activeTab === "assignments"
                ? "All Assignments"
                : "All Notes"}
            </h4>
            <div className="d-flex align-items-center flex-wrap">
              {!isUserStudent && isNonTeacher && (
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom pb-0">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Class</label>
                              <CommonSelect
                                className="select"
                                options={classOptions}
                                value={
                                  classOptions.find(
                                    (option) => option.value === filters.class
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  handleFilterChange(
                                    "class",
                                    selectedOption?.value || ""
                                  )
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={sectionOptions}
                                value={
                                  sectionOptions.find(
                                    (option) => option.value === filters.section
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  handleFilterChange(
                                    "section",
                                    selectedOption?.value || ""
                                  )
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Course</label>
                              <CommonSelect
                                className="select"
                                options={courseOptions}
                                value={
                                  courseOptions.find(
                                    (option) => option.value === filters.course
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  handleFilterChange(
                                    "course",
                                    selectedOption?.value || ""
                                  )
                                }
                              />
                            </div>
                          </div>
                          {activeTab === "assignments" && (
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Type</label>
                                <CommonSelect
                                  className="select"
                                  options={typeOptions}
                                  value={
                                    typeOptions.find(
                                      (option) => option.value === filters.type
                                    ) || null
                                  }
                                  isSearchable={true}
                                  onChange={(selectedOption) =>
                                    handleFilterChange(
                                      "type",
                                      selectedOption?.value || ""
                                    )
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={() =>
                            setFilters({
                              class: classIdFromUrl || "",
                              section: "",
                              course: "",
                              type: "",
                            })
                          }
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              <div className="dropdown mb-3">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-sort-ascending-2 me-2" />
                  Sort by {sortBy.label}
                </Link>
                <ul className="dropdown-menu p-3">
                  {CLASSES_SORT_OPTIONS.map((item, index) => (
                    <li key={index}>
                      <div
                        className={`dropdown-item rounded-1 ${
                          item.label === sortBy.label ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSortSelect(item)}
                      >
                        {item.label}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div
            className="card-body p-0 py-3 printable-content"
            ref={componentRef}
          >
            <div className="print-only-header">
              <div className="school-brand">
                <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
              </div>
              <div className="report-title">Class Homework</div>
              <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
            </div>
            {isUserStudent && enrollmentLoading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading your class details...</p>
              </div>
            ) : isUserStudent && !studentClassId ? (
              <div className="text-center p-5">
                <i
                  className="ti ti-school text-warning mb-3"
                  style={{ fontSize: "3rem" }}
                ></i>
                <h4 className="text-warning mb-2">No Class Found</h4>
                <p className="text-muted mb-0">
                  You are not enrolled in any class for the current session.
                </p>
              </div>
            ) : (
              <Table
                columns={
                  activeTab === "assignments" ? assignmentColumns : notesColumns
                }
                dataSource={activeTab === "assignments" ? assignments : notes}
                loading={loading}
                setSearchText={setSearchText}
              />
            )}
          </div>
        </div>
      </div>

      {!isUserStudent && (
        <>
          {/* Add Assignment Modal */}
          <div className="modal fade" id="add_home_work">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">Add Assignment</h4>
                  <button
                    type="button"
                    className="btn-close custom-btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={resetAssignmentForm}
                  >
                    <i className="ti ti-x" />
                  </button>
                </div>
                <form onSubmit={handleAssignmentSubmit}>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Title <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={assignmentForm.title}
                            onChange={(e) =>
                              setAssignmentForm({
                                ...assignmentForm,
                                title: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Type <span className="text-danger">*</span>
                              </label>
                              <CommonSelect
                                className="select"
                                options={typeOptions}
                                value={
                                  typeOptions.find(
                                    (option) =>
                                      option.value === assignmentForm.type
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  setAssignmentForm({
                                    ...assignmentForm,
                                    type: selectedOption?.value || "assignment",
                                  })
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Class <span className="text-danger">*</span>
                              </label>
                              <CommonSelect
                                className="select"
                                options={classOptions}
                                value={
                                  classOptions.find(
                                    (option) =>
                                      option.value === assignmentForm.class
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  setAssignmentForm({
                                    ...assignmentForm,
                                    class: selectedOption?.value || "",
                                  })
                                }
                                required
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={sectionOptions}
                                value={
                                  sectionOptions.find(
                                    (option) =>
                                      option.value === assignmentForm.section
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  setAssignmentForm({
                                    ...assignmentForm,
                                    section: selectedOption?.value || "",
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Course <span className="text-danger">*</span>
                              </label>
                              <CommonSelect
                                className="select"
                                options={formCourseOptions}
                                value={
                                  formCourseOptions.find(
                                    (option) =>
                                      option.value === assignmentForm.course
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  setAssignmentForm({
                                    ...assignmentForm,
                                    course: selectedOption?.value || "",
                                  })
                                }
                                required
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Due Date</label>
                              <input
                                type="date"
                                className="form-control"
                                value={assignmentForm.due_date}
                                onChange={(e) =>
                                  setAssignmentForm({
                                    ...assignmentForm,
                                    due_date: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Attachment{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                key={fileInputKey}
                                type="file"
                                className="form-control"
                                onChange={handleFileChange}
                                required
                              />
                              <small className="text-muted">
                                PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX files
                                allowed (max 10MB)
                              </small>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Instructions</label>
                          <textarea
                            className="form-control"
                            rows={4}
                            value={assignmentForm.feedback}
                            onChange={(e) =>
                              setAssignmentForm({
                                ...assignmentForm,
                                feedback: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="status-title">
                            <h5>Status</h5>
                            <p>Change the Status by toggle </p>
                          </div>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={assignmentForm.status === "active"}
                              onChange={(e) =>
                                setAssignmentForm({
                                  ...assignmentForm,
                                  status: e.target.checked
                                    ? "active"
                                    : "inactive",
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                      onClick={resetAssignmentForm}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading || !selectedFile}
                    >
                      {loading ? "Adding..." : "Add Assignment"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Edit Assignment Modal */}
          <div className="modal fade" id="edit_home_work">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">Edit Assignment</h4>
                  <button
                    type="button"
                    className="btn-close custom-btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={resetAssignmentForm}
                  >
                    <i className="ti ti-x" />
                  </button>
                </div>
                <form onSubmit={handleAssignmentUpdate}>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Title <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={assignmentForm.title}
                            onChange={(e) =>
                              setAssignmentForm({
                                ...assignmentForm,
                                title: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Type <span className="text-danger">*</span>
                              </label>
                              <CommonSelect
                                className="select"
                                options={typeOptions}
                                value={
                                  typeOptions.find(
                                    (option) =>
                                      option.value === assignmentForm.type
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  setAssignmentForm({
                                    ...assignmentForm,
                                    type: selectedOption?.value || "assignment",
                                  })
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Class <span className="text-danger">*</span>
                              </label>
                              <CommonSelect
                                className="select"
                                options={classOptions}
                                value={
                                  classOptions.find(
                                    (option) =>
                                      option.value === assignmentForm.class
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  setAssignmentForm({
                                    ...assignmentForm,
                                    class: selectedOption?.value || "",
                                  })
                                }
                                required
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={sectionOptions}
                                value={
                                  sectionOptions.find(
                                    (option) =>
                                      option.value === assignmentForm.section
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  setAssignmentForm({
                                    ...assignmentForm,
                                    section: selectedOption?.value || "",
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Course <span className="text-danger">*</span>
                              </label>
                              <CommonSelect
                                className="select"
                                options={formCourseOptions}
                                value={
                                  formCourseOptions.find(
                                    (option) =>
                                      option.value === assignmentForm.course
                                  ) || null
                                }
                                isSearchable={true}
                                onChange={(selectedOption) =>
                                  setAssignmentForm({
                                    ...assignmentForm,
                                    course: selectedOption?.value || "",
                                  })
                                }
                                required
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Due Date</label>
                              <input
                                type="date"
                                className="form-control"
                                value={assignmentForm.due_date}
                                onChange={(e) =>
                                  setAssignmentForm({
                                    ...assignmentForm,
                                    due_date: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Attachment{" "}
                                <span className="text-danger">*</span>
                              </label>
                              {currentFileInfo.url && (
                                <div className="mb-2 p-2 border rounded bg-light">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div
                                      className="text-truncate"
                                      style={{ maxWidth: "70%" }}
                                    >
                                      <small className="text-muted d-block">
                                        Current File:
                                      </small>
                                      <span
                                        className="text-dark"
                                        title={currentFileInfo.name}
                                      >
                                        {currentFileInfo.name}
                                      </span>
                                    </div>
                                    <a
                                      href={currentFileInfo.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-primary flex-shrink-0"
                                    >
                                      <i className="ti ti-eye me-1"></i>
                                      View
                                    </a>
                                  </div>
                                </div>
                              )}
                              <input
                                key={fileInputKey}
                                type="file"
                                className="form-control mt-2"
                                onChange={handleFileChange}
                                required={!currentFileInfo.url}
                              />
                              <small className="text-muted">
                                {currentFileInfo.url
                                  ? "Select new file to replace current file (optional)"
                                  : "PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX files allowed (max 10MB) *"}
                              </small>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Instructions</label>
                          <textarea
                            className="form-control"
                            rows={4}
                            value={assignmentForm.feedback}
                            onChange={(e) =>
                              setAssignmentForm({
                                ...assignmentForm,
                                feedback: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="status-title">
                            <h5>Status</h5>
                            <p>Change the Status by toggle </p>
                          </div>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={assignmentForm.status === "active"}
                              onChange={(e) =>
                                setAssignmentForm({
                                  ...assignmentForm,
                                  status: e.target.checked
                                    ? "active"
                                    : "inactive",
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                      onClick={resetAssignmentForm}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={
                        loading || (!selectedFile && !currentFileInfo.url)
                      }
                    >
                      {loading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Add Note Modal */}
          {(isUserTeacher || isNonTeacher) && (
            <>
              <div className="modal fade" id="add_note">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h4 className="modal-title">Add Note</h4>
                      <button
                        type="button"
                        className="btn-close custom-btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                        onClick={resetNoteForm}
                      >
                        <i className="ti ti-x" />
                      </button>
                    </div>
                    <form onSubmit={handleNoteSubmit}>
                      <div className="modal-body">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Title <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={noteForm.title}
                                onChange={(e) =>
                                  setNoteForm({
                                    ...noteForm,
                                    title: e.target.value,
                                  })
                                }
                                required
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label">
                                Teacher <span className="text-danger">*</span>
                              </label>
                              {isUserTeacher ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={`${user.firstName} ${user.lastName}`}
                                  readOnly
                                />
                              ) : (
                                <CommonSelect
                                  className="select"
                                  options={teacherOptions}
                                  value={
                                    teacherOptions.find(
                                      (option) =>
                                        option.value === noteForm.teacher
                                    ) || null
                                  }
                                  isSearchable={true}
                                  onChange={(selectedOption) =>
                                    setNoteForm({
                                      ...noteForm,
                                      teacher: selectedOption?.value || "",
                                    })
                                  }
                                  required
                                />
                              )}
                            </div>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Class <span className="text-danger">*</span>
                                  </label>
                                  <CommonSelect
                                    className="select"
                                    options={classOptions}
                                    value={
                                      classOptions.find(
                                        (option) =>
                                          option.value === noteForm.class
                                      ) || null
                                    }
                                    isSearchable={true}
                                    onChange={(selectedOption) =>
                                      setNoteForm({
                                        ...noteForm,
                                        class: selectedOption?.value || "",
                                      })
                                    }
                                    required
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">Section</label>
                                  <CommonSelect
                                    className="select"
                                    options={sectionOptions}
                                    value={
                                      sectionOptions.find(
                                        (option) =>
                                          option.value === noteForm.section
                                      ) || null
                                    }
                                    isSearchable={true}
                                    onChange={(selectedOption) =>
                                      setNoteForm({
                                        ...noteForm,
                                        section: selectedOption?.value || "",
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Course{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <CommonSelect
                                    className="select"
                                    options={courseOptions}
                                    value={
                                      courseOptions.find(
                                        (option) =>
                                          option.value === noteForm.course
                                      ) || null
                                    }
                                    isSearchable={true}
                                    onChange={(selectedOption) =>
                                      setNoteForm({
                                        ...noteForm,
                                        course: selectedOption?.value || "",
                                      })
                                    }
                                    required
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Attachment{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    key={fileInputKey}
                                    type="file"
                                    className="form-control"
                                    onChange={handleFileChange}
                                    required
                                  />
                                  <small className="text-muted">
                                    PDF, DOC, DOCX, PPT, PPTX files allowed (max
                                    10MB)
                                  </small>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="status-title">
                                <h5>Status</h5>
                                <p>Change the Status by toggle </p>
                              </div>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  checked={noteForm.status === "active"}
                                  onChange={(e) =>
                                    setNoteForm({
                                      ...noteForm,
                                      status: e.target.checked
                                        ? "active"
                                        : "inactive",
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-light me-2"
                          data-bs-dismiss="modal"
                          onClick={resetNoteForm}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading || !selectedFile}
                        >
                          {loading ? "Adding..." : "Add Note"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              {/* Edit Note Modal */}
              <div className="modal fade" id="edit_note">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h4 className="modal-title">Edit Note</h4>
                      <button
                        type="button"
                        className="btn-close custom-btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                        onClick={resetNoteForm}
                      >
                        <i className="ti ti-x" />
                      </button>
                    </div>
                    <form onSubmit={handleNoteUpdate}>
                      <div className="modal-body">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Title <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={noteForm.title}
                                onChange={(e) =>
                                  setNoteForm({
                                    ...noteForm,
                                    title: e.target.value,
                                  })
                                }
                                required
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label">
                                Teacher <span className="text-danger">*</span>
                              </label>
                              {isUserTeacher ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={`${user.firstName} ${user.lastName}`}
                                  readOnly
                                  style={{
                                    backgroundColor: "#f8f9fa",
                                    cursor: "not-allowed",
                                  }}
                                />
                              ) : (
                                <CommonSelect
                                  className="select"
                                  options={teacherOptions}
                                  value={
                                    teacherOptions.find(
                                      (option) =>
                                        option.value === noteForm.teacher
                                    ) || null
                                  }
                                  isSearchable={true}
                                  onChange={(selectedOption) =>
                                    setNoteForm({
                                      ...noteForm,
                                      teacher: selectedOption?.value || "",
                                      teacherName: selectedOption?.label || "",
                                    })
                                  }
                                  required
                                />
                              )}
                            </div>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Class <span className="text-danger">*</span>
                                  </label>
                                  <CommonSelect
                                    className="select"
                                    options={classOptions}
                                    value={
                                      classOptions.find(
                                        (option) =>
                                          option.value === noteForm.class
                                      ) || null
                                    }
                                    isSearchable={true}
                                    onChange={(selectedOption) =>
                                      setNoteForm({
                                        ...noteForm,
                                        class: selectedOption?.value || "",
                                      })
                                    }
                                    required
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">Section</label>
                                  <CommonSelect
                                    className="select"
                                    options={sectionOptions}
                                    value={
                                      sectionOptions.find(
                                        (option) =>
                                          option.value === noteForm.section
                                      ) || null
                                    }
                                    isSearchable={true}
                                    onChange={(selectedOption) =>
                                      setNoteForm({
                                        ...noteForm,
                                        section: selectedOption?.value || "",
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Course{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <CommonSelect
                                    className="select"
                                    options={courseOptions}
                                    value={
                                      courseOptions.find(
                                        (option) =>
                                          option.value === noteForm.course
                                      ) || null
                                    }
                                    isSearchable={true}
                                    onChange={(selectedOption) =>
                                      setNoteForm({
                                        ...noteForm,
                                        course: selectedOption?.value || "",
                                      })
                                    }
                                    required
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Attachment{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  {currentFileInfo.url && (
                                    <div className="mb-2 p-2 border rounded bg-light">
                                      <div className="d-flex align-items-center justify-content-between">
                                        <div
                                          className="text-truncate"
                                          style={{ maxWidth: "70%" }}
                                        >
                                          <small className="text-muted d-block">
                                            Current File:
                                          </small>
                                          <span
                                            className="text-dark"
                                            title={currentFileInfo.name}
                                          >
                                            {currentFileInfo.name}
                                          </span>
                                        </div>
                                        <a
                                          href={currentFileInfo.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="btn btn-sm btn-outline-primary flex-shrink-0"
                                        >
                                          <i className="ti ti-eye me-1"></i>
                                          View
                                        </a>
                                      </div>
                                    </div>
                                  )}
                                  <input
                                    key={fileInputKey}
                                    type="file"
                                    className="form-control mt-2"
                                    onChange={handleFileChange}
                                    required={!currentFileInfo.url}
                                  />
                                  <small className="text-muted">
                                    {currentFileInfo.url
                                      ? "Select new file to replace current file (optional)"
                                      : "PDF, DOC, DOCX, PPT, PPTX files allowed (max 10MB) *"}
                                  </small>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="status-title">
                                <h5>Status</h5>
                                <p>Change the Status by toggle </p>
                              </div>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  checked={noteForm.status === "active"}
                                  onChange={(e) =>
                                    setNoteForm({
                                      ...noteForm,
                                      status: e.target.checked
                                        ? "active"
                                        : "inactive",
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-light me-2"
                          data-bs-dismiss="modal"
                          onClick={resetNoteForm}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={
                            loading || (!selectedFile && !currentFileInfo.url)
                          }
                        >
                          {loading ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Delete Modal */}
          <div className="modal fade" id="delete-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    You want to delete this item? This action cannot be undone.
                  </p>
                  <div className="d-flex justify-content-center">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        if (activeTab === "assignments" && editingAssignment) {
                          handleAssignmentDelete(editingAssignment._id);
                        } else if (activeTab === "notes" && editingNote) {
                          handleNoteDelete(editingNote._id);
                        }
                      }}
                      disabled={loading}
                    >
                      {loading ? "Deleting..." : "Yes, Delete"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClassHomeWork;
